<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { UiButton, UiText, useApi } from "@shopexperts/shared";

const route = useRoute();
const api = useApi();

const isLoading = ref(false);
const errorMessage = ref<string | null>(null);
const checkoutUrl = ref<string | null>(null);

const quoteId = computed(() => {
  const raw = Array.isArray(route.query.quote) ? route.query.quote[0] : route.query.quote;
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
});

async function prepareCheckout() {
  if (!quoteId.value) {
    errorMessage.value = "This checkout link is invalid.";
    return;
  }

  isLoading.value = true;
  errorMessage.value = null;

  try {
    const response = await api.client.projectsV2.createQuoteCheckoutSession({
      params: { quoteId: quoteId.value },
    });
    checkoutUrl.value = response.checkout_url;
    window.location.replace(response.checkout_url);
  } catch (error) {
    errorMessage.value = error instanceof Error
      ? error.message
      : "We could not prepare checkout for this quote. Please try again.";
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  void prepareCheckout();
});
</script>

<template>
  <main class="min-h-screen bg-gray-50 px-6 py-16">
    <section class="mx-auto max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
      <div class="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
        <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <path d="M5 12h14M13 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>

      <UiText as="h1" variant="h3" class="mb-2 text-gray-900">
        Preparing your secure checkout
      </UiText>
      <UiText v-if="isLoading && !errorMessage" as="p" variant="p-small" class="text-gray-500">
        Please wait while we create a fresh Stripe checkout session.
      </UiText>

      <template v-if="errorMessage">
        <UiText as="p" variant="p-small" class="mt-3 text-red-600">
          {{ errorMessage }}
        </UiText>
        <UiButton class="mt-5" variant="primary" label="Try again" @click="prepareCheckout" />
      </template>

      <UiText v-else-if="checkoutUrl" as="p" variant="p-small" class="mt-3 text-gray-500">
        If you are not redirected automatically, use the button below.
      </UiText>
      <UiButton
        v-if="checkoutUrl"
        class="mt-5"
        variant="primary"
        label="Continue to checkout"
        :href="checkoutUrl"
        rel="noopener noreferrer"
      />
    </section>
  </main>
</template>
